import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { FlexTimeActivity, FlexTimePeriod } from 'libs/domain';
import { BehaviorSubject, ReplaySubject, combineLatest, lastValueFrom } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';

import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { RootService } from '@app/src/app/minimal/services/RootService';
import { FormSelectOption } from '@app/src/app/shared/components/form';
import {
  SystemAlertCloseEvents,
  SystemAlertModalService,
  SystemAlertModalType,
} from '@app/src/app/shared/components/system-alert-modal';

import { ActivityCardFields } from '@modules/flex-time-manager/components/ftm-shared/ftm-shared.constants';

import {
  MODAL_OVERLAY_DATA,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import {
  FlexTimeActivityInstanceService,
  FlexTimeActivityService,
  FlexTimePeriodService,
} from '@shared/services/flex-time';
import { FlexTimePermissionsService } from '@shared/services/flex-time/flex-time-permissions';
import { MediaService } from '@shared/services/media';

import { FlexTimeManagerRoutes } from '../../../../constants';
import { FtmPeriodsActivityEditMessage } from '../../constants';
import { FtmPeriodsActivityEditModalData, FtmPeriodsActivityEditModalResponse } from '../../types';

@Component({
  selector: 'mg-ftm-periods-activity-edit',
  templateUrl: './ftm-periods-activity-edit.component.html',
  styleUrls: ['./ftm-periods-activity-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtmPeriodsActivityEditComponent implements OnInit, OnDestroy {
  /** Constants */
  public readonly MSG = FtmPeriodsActivityEditMessage;

  /** General Observables */
  private _destroyed = new ReplaySubject<void>(1);

  /** Teacher View */
  private _isAdminView = this._flexPermissionService.isFlexTimeAdmin();

  public readonly form: FormGroup<{
    flexTimeActivity: FormControl<FlexTimeActivity>;
    searchFilter: FormControl<string>;
    teacherFilter: FormControl<string>;
  }> = this._fb.group({
    flexTimeActivity: [undefined, Validators.required],
    searchFilter: [''],
    teacherFilter: [this.dialogData.myActivities ? this._authInfoService.authPersonHash : null],
  });

  /** FlexTime Period */
  private readonly _period = new BehaviorSubject<FlexTimePeriod>(null);
  private _isLoadingPeriodData = new BehaviorSubject<boolean>(false);
  public readonly isLoadingPeriodData$ = this._isLoadingPeriodData.asObservable();

  /** FlexTime Activities */
  private readonly _activities = new BehaviorSubject<FlexTimeActivity[] | undefined>(undefined);

  private readonly _activities$ = this._activities.asObservable().pipe(shareReplay());
  private _isLoadingActivities = new BehaviorSubject<boolean>(false);
  public readonly isLoadingActivities$ = this._isLoadingActivities.asObservable();

  public filteredActivities$ = combineLatest([
    this._activities$,
    this.form.valueChanges.pipe(startWith(this.form.value), debounceTime(100)),
  ]).pipe(
    map(([activities, form]) => {
      const { searchFilter, teacherFilter } = form;

      const filterFunctions = [];
      const filterBySearch = a => a.name.toLowerCase().includes(searchFilter.trim().toLowerCase());

      if (searchFilter) filterFunctions.push(filterBySearch);

      const filtered = (activities || []).reduce((result, obj) => {
        if (filterFunctions.every(filterFn => filterFn(obj))) {
          result.push(obj);
        }
        return result;
      }, []);

      return filtered;
    }),
  );

  public teacherOptions$ = this._activities$.pipe(
    map(activities => {
      const teacherOptions: FormSelectOption<string>[] = (activities || []).map(a => ({
        value: a.createdByPerson.hash,
        label: a.createdByPerson.name,
      }));

      const deduped = Array.from(new Map(teacherOptions.map(item => [item.value, item])).values());

      return deduped;
    }),
  );

  private _existingActivitySubj = new BehaviorSubject<FlexTimeActivity>(null);
  public existingActivity$ = this._existingActivitySubj.asObservable();

  /** Loading */
  public readonly isLoading$ = combineLatest([this.isLoadingPeriodData$, this.isLoadingActivities$]).pipe(
    map(loading => loading.every(v => v)),
  );

  public CARD_FIELDS = [
    ActivityCardFields.NAME,
    ActivityCardFields.DESCRIPTION,
    ActivityCardFields.LOCATION,
    ActivityCardFields.TEACHER,
    ActivityCardFields.SEATS,
  ];

  /** Component Constructor */
  constructor(
    @Inject(MODAL_OVERLAY_DATA)
    public dialogData: FtmPeriodsActivityEditModalData,
    private _modalRef: ModalOverlayRef<FtmPeriodsActivityEditModalResponse, FtmPeriodsActivityEditModalData>,
    public media: MediaService,
    private _fb: UntypedFormBuilder,
    private _router: Router,
    private _ftPeriod: FlexTimePeriodService,
    private _ftActivity: FlexTimeActivityService,
    private _ftActivityInstance: FlexTimeActivityInstanceService,
    private _authInfoService: AuthInfoService,
    private _flexPermissionService: FlexTimePermissionsService,
    private _rootService: RootService,
    private _systemAlertModal: SystemAlertModalService,
  ) {}

  ngOnInit(): void {
    this.form.controls.teacherFilter.valueChanges.pipe(takeUntil(this._destroyed)).subscribe(teacherFilter => {
      this._getActivitiesData(teacherFilter);
    });

    this._getActivitiesData();
    this._getPeriodData(this.dialogData.id);
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
    this._period.complete();
    this._isLoadingActivities.complete();
    this._isLoadingPeriodData.complete();
    this._activities.complete();
  }

  public setActive(activity) {
    const activityControl = this.form.controls.flexTimeActivity;
    activityControl.setValue(activity);
    activityControl.markAsDirty();
  }

  public async submit(): Promise<void> {
    if (!this.form.valid) return;

    const flexTimeActivity = this.form.controls.flexTimeActivity.value;
    const payload = {
      flexTimeActivity,
      flexTimePeriodId: this.dialogData.id,
      flexTimeActivityId: this.form.controls.flexTimeActivity.value.id,
    };

    const period = this._period.value;
    let willReplace = false;

    if (this._authInfoService.authPersonHash !== flexTimeActivity.createdByPerson.hash) {
      for (const activity of period.activityInstances) {
        if (activity.createdBy === flexTimeActivity.createdBy) {
          willReplace = true;
        }
      }
    }

    if (willReplace) {
      const modalRef = await this._systemAlertModal.open({
        modalType: SystemAlertModalType.WARNING,
        heading: 'Replace Activity',
        message: `Choosing this activity will replace this teacher's other activity in this period. Are you sure you want to do that?`,
        confirmActionBtn: 'Confirm',
      });

      const response = await lastValueFrom(modalRef.afterClosed());

      if (response?.type === SystemAlertCloseEvents.CONFIRM) {
        await this._rootService.addLoadingPromise(this._createInstance(payload));
      }
    } else {
      await this._rootService.addLoadingPromise(this._createInstance(payload));
    }
  }

  private async _createInstance(payload) {
    await this._ftActivityInstance.create(payload).then(instance => {
      this._modalRef.close(ModalOverlayServiceCloseEventType.CREATE, {
        instance,
      });
    });
  }

  public async remove() {
    const modalRef = await this._systemAlertModal.open({
      modalType: SystemAlertModalType.WARNING,
      heading: 'Remove Activity',
      message: `Are you sure you want to remove this activity?`,
      confirmActionBtn: 'Confirm',
    });

    const response = await lastValueFrom(modalRef.afterClosed());

    if (response?.type === SystemAlertCloseEvents.CONFIRM) {
      this._modalRef.close(ModalOverlayServiceCloseEventType.DELETE);
    }
  }

  public async cancel(): Promise<void> {
    this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
  }

  public goToActivityTemplate() {
    this._modalRef.close(ModalOverlayServiceCloseEventType.NAVIGATE);
    this._router.navigate(['./', FlexTimeManagerRoutes.ROOT, FlexTimeManagerRoutes.ACTIVITY_TEMPLATES]);
  }

  private async _getActivitiesData(personHash?: string): Promise<void> {
    try {
      this._isLoadingActivities.next(true);

      // if hash is provided through filter, fetch only that user's activities
      const hash =
        personHash ??
        (this._isAdminView && !this.dialogData.myActivities ? undefined : this._authInfoService.authPersonHash);

      const result = await this._ftActivity.fetchAll(hash);
      this._activities.next([...result]);
    } catch (error) {
      this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
    } finally {
      this._isLoadingActivities.next(false);
    }
  }

  private async _getPeriodData(id: number): Promise<void> {
    if (!id) return;
    try {
      this._isLoadingPeriodData.next(true);
      const results = await this._ftPeriod.fetch(id);
      if (results.activityInstances?.length && this.dialogData?.currentInstanceId) {
        const instance = results.activityInstances.find(a => a.id === this.dialogData?.currentInstanceId);

        if (instance) {
          this._existingActivitySubj.next(instance?.flexTimeActivity);
        }
      }
      this._period.next(results);
    } catch (error) {
    } finally {
      this._isLoadingPeriodData.next(false);
    }
  }
}
